import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, MessageCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, getErrorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

// Relative "x min/hour/day ago" from an ISO timestamp.
const relTime = (iso) => {
  if (!iso) return "";
  const secs = Math.max(1, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (secs < 3600) return `${Math.floor(secs / 60)} min ago`;
  if (secs < 86400) return `${Math.floor(secs / 3600)}h ago`;
  return `${Math.floor(secs / 86400)}d ago`;
};

// Manual, on-demand WhatsApp "call back" nudge. Recruiter-only, no status change.
// Shows last-nudged history and disables during the server cooldown window.
export const WhatsAppCallbackButton = ({ candidateName, candidateMobile, size = "sm", className = "" }) => {
  const { user } = useAuth();
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState(null); // {last_nudged_at, last_nudged_by, cooldown_hours}
  const isRecruiter = user?.role === "recruiter";

  const loadStatus = useCallback(() => {
    if (!isRecruiter || !candidateMobile) return;
    api.get("/whatsapp/nudge-status", { params: { mobile: candidateMobile } })
      .then((r) => setStatus(r.data))
      .catch(() => {});
  }, [isRecruiter, candidateMobile]);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  if (!isRecruiter) return null;

  const onCooldown = status?.last_nudged_at
    ? (Date.now() - new Date(status.last_nudged_at).getTime()) < (status.cooldown_hours || 3) * 3600 * 1000
    : false;

  const send = async () => {
    if (!candidateMobile) return toast.error("No candidate mobile number on file");
    setBusy(true);
    try {
      const { data } = await api.post("/whatsapp/callback-nudge", {
        candidate_name: candidateName || "there",
        candidate_mobile: candidateMobile,
      });
      toast.success(data.dry_run
        ? `WhatsApp nudge wired correctly (preview dry-run — no real message sent to ${candidateName || "candidate"})`
        : `WhatsApp call-back sent to ${candidateName || "candidate"}`);
      setStatus((s) => ({ ...(s || {}), last_nudged_at: data.last_nudged_at, last_nudged_by: data.last_nudged_by }));
    } catch (err) {
      if (err?.response?.status === 429) toast.error(err.response.data.detail);
      else toast.error(getErrorMessage(err));
      loadStatus();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="inline-flex flex-col items-start gap-0.5">
      <Button variant="outline" size={size} onClick={send} disabled={busy || onCooldown}
        data-testid="whatsapp-callback-btn"
        title={onCooldown ? "Recently nudged — try again later" : "Send a WhatsApp call-back reminder"}
        className={`rounded-lg gap-1.5 bg-white text-emerald-700 hover:text-emerald-800 hover:bg-emerald-50 border-emerald-200 ${className}`}>
        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <MessageCircle className="h-3.5 w-3.5" />}
        WhatsApp Call Back
      </Button>
      {status?.last_nudged_at && (
        <span className="text-[11px] text-muted-foreground" data-testid="whatsapp-nudge-history">
          Nudged {relTime(status.last_nudged_at)}{status.last_nudged_by ? ` · ${status.last_nudged_by}` : ""}
        </span>
      )}
    </div>
  );
};
